import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { Box, Button, Drawer, Flex, LoadingOverlay } from "@mantine/core";
import { ViewState } from "react-map-gl";

import MapCanvas, { MapCanvasHandle } from "./components/MapCanvas";
import BaseballCard, { Tab } from "../PractitionerBaseballCard";
import RoutesTab from "../PractitionerBaseballCard/components/RoutesTab";
import { NavigationService, NavigationApp, resolveNavigationLocation } from "../../helpers/navigation";
import { store } from 'stores/Store';

import { ReactComponent as NavigationArrowIcon } from "assets/navigationArrowRight.svg";
import { ReactComponent as BlueExpandIcon } from 'assets/blueExpand.svg';
import { ReactComponent as CheckInIcon } from "assets/check_in.svg";
import { ReactComponent as LeftIcon } from "assets/caretleft.svg";

import styles from './map.module.scss';
import ViewRoute from "../MapRouter/modules/ViewRoute";

export const DEFAULT_PIN_COLOR = "#7E7E7E";

export interface Pin {
  id: number;
  latLon: [number, number];
  color?: string;
}

const NavigationMode = observer(() => {
  const navigate = useNavigate();

  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [viewState, setViewState] = useState<ViewState>({
    latitude: 0,
    longitude: 0,
    zoom: 6,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  });
  const [routePins, setRoutePins] = useState<Pin[]>([]);
  const [routeData, setRouteData] = useState<number[][]>();
  const [selectedPractitionerIndex, setSelectedPractitionerIndex] = useState<number>(-1);
  const [isRouteListDrawerOpen, setIsRouteListDrawerOpen] = useState(false);
  const [isPractitionerCardOpen, setIsPractitionerCardOpen] = useState(false);
  const [initialPractitionerCardTab, setInitialPractitionerCardTab] = useState(Tab.SUMMARY);
  const [isGoRouteModalOpen, setIsGoRouteModalOpen] = useState(false);

  const mapCanvasRef = useRef<MapCanvasHandle>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedPractitioner = useMemo(() => selectedPractitionerIndex >= 0 && selectedPractitionerIndex < store.selectedPractitioners.length ? store.selectedPractitioners.at(selectedPractitionerIndex) : undefined, [selectedPractitionerIndex]);

  const navigateToPreviousPractitioner = () => {
    setSelectedPractitionerIndex((prev) => prev - 1);
  };

  const navigateToNextPractitioner = () => {
    setSelectedPractitionerIndex((prev) => prev + 1);
  };

  const openPractitionerCardSummary = () => {
    if (!selectedPractitioner) return;

    store.setCurrentPractitioner(selectedPractitioner);
    setIsPractitionerCardOpen(true);
    setInitialPractitionerCardTab(Tab.SUMMARY);
  };

  const openPractitionerCardCheckin = () => {
    if (!selectedPractitioner) return;

    store.setCurrentPractitioner(selectedPractitioner);
    setIsPractitionerCardOpen(true);
    setInitialPractitionerCardTab(Tab.CHECK_IN);
  };

  const closeNavigationMode = () => {
    navigate('/mapView');
    store.setIsSlideUpMenusOpenInMap(true);
  };

  const handleGoRoute = (opt: string) => {
    if (!selectedPractitioner) return;

    // EIS-979 Part 1: same coords-FIRST navigation as the baseball card and
    // map minicard, via the shared NavigationService (address search only when
    // no usable lat/lng exists). Handles route start/end locations and
    // practitioners (default-address FK, flat lat/lng fallback) uniformly.
    const location = resolveNavigationLocation(selectedPractitioner);
    if (!location) {
      console.log('No navigation data available for selection');
      return;
    }
    NavigationService.navigateToLocation(location, opt as NavigationApp, {
      onError: (error) => console.error(`${opt} navigation failed:`, error),
    });
    setIsGoRouteModalOpen(false);
  };

  useEffect(() => {
    const pins: Pin[] = [];

    store.selectedPractitioners
      .forEach((p, index) => {
        const lon = parseFloat(String(p.longitude));
        const lat = parseFloat(String(p.latitude));
        pins.push({
          id: p.isFirstLocation || p.isLastLocation ? index + 1 : p.id,
          latLon: [lat, lon]
        });
      });

    setRoutePins(pins);
    setSelectedPractitionerIndex(pins.length ? 0 : -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (store?.currentDirections) {
      if (store?.currentDirections.length > 0) {
        setRouteData(store?.currentDirections);
      } else {
        setRouteData(undefined);
      }
    } else {
      setRouteData(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.currentDirections]);

  useEffect(() => {
    if (!selectedPractitioner) return;

    setViewState((prev) => ({
      ...prev,
      latitude: selectedPractitioner.latitude,
      longitude: selectedPractitioner.longitude,
      zoom: 14,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPractitioner])

  return (
    <>
      <div className={styles.container}>
        <LoadingOverlay
          visible={!isMapLoaded}
          zIndex={1000}
        />
        <Flex
          pos="absolute"
          top={0}
          left={0}
          right={0}
          p={10}
          align="center"
          justify="center"
          className={styles.header}
        >
          <span>NAVIGATION MODE</span>
        </Flex>
        <Button
          className={styles.endNavigationButton}
          onClick={closeNavigationMode}
        >
          END NAVIGATION MODE
        </Button>
        {selectedPractitioner && (
          <Box className={styles.toolbox}>
            <Flex className={styles.toolboxTopSection}>
              {selectedPractitioner.isFirstLocation ? (
                <div className={styles.startEndLocationContainer}>
                  <div className={styles.startEndLocationLabel}>
                    Starting Location
                  </div>
                  <div className={styles.address}>
                    {selectedPractitioner.address}
                  </div>
                </div>
              ) : selectedPractitioner.isLastLocation ? (
                <div className={styles.startEndLocationContainer}>
                  <div className={styles.startEndLocationLabel}>
                    Ending Location
                  </div>
                  <div className={styles.address}>
                    {selectedPractitioner.address}
                  </div>
                </div>
              ) : (
                <Button
                  variant="transparent"
                  justify="space-between"
                  rightSection={<BlueExpandIcon height={18} />}
                  classNames={{
                    root: styles.practitionerButton,
                    section: styles.buttonSection,
                  }}
                  onClick={openPractitionerCardSummary}
                >
                  <div>
                    <div className={styles.practitionerName}>{selectedPractitioner.name}</div>
                    <div className={styles.address}>{selectedPractitioner.addresses && selectedPractitioner.addresses.length > 0 ? selectedPractitioner.addresses[0].address : 'No address available'}</div>
                  </div>
                </Button>
              )}
              <Flex flex="0 0 auto">
                <Button
                  variant="transparent"
                  h="100%"
                  w={32}
                  px={8}
                  onClick={() => setIsGoRouteModalOpen(true)}
                >
                  <NavigationArrowIcon stroke="#4F7D9C" width={22} height={22} />
                </Button>
                {!selectedPractitioner.isFirstLocation && !selectedPractitioner.isLastLocation && (
                  <Button
                    variant="transparent"
                    h="100%"
                    w={32}
                    px={8}
                    onClick={openPractitionerCardCheckin}
                  >
                    <CheckInIcon stroke="#4F7D9C" width={30} height={30} />
                  </Button>
                )}
              </Flex>
            </Flex>
            <Flex>
              <Button
                variant="transparent"
                leftSection={<LeftIcon stroke="#4F7D9C" />}
                classNames={{
                  section: styles.buttonSection,
                }}
                disabled={selectedPractitionerIndex === undefined || selectedPractitionerIndex <= 0}
                onClick={navigateToPreviousPractitioner}
              >
                Prev
              </Button>
              <Button
                variant="transparent"
                flex={1}
                onClick={() => setIsRouteListDrawerOpen(true)}
              >
                Route List
              </Button>
              <Button
                variant="transparent"
                rightSection={<LeftIcon stroke="#4F7D9C" style={{ transform: 'rotate(180deg)' }} />}
                classNames={{
                  section: styles.buttonSection,
                }}
                disabled={selectedPractitionerIndex === undefined || selectedPractitionerIndex >= store.selectedPractitioners.length - 1}
                onClick={navigateToNextPractitioner}
              >
                Next
              </Button>
            </Flex>
          </Box>
        )}
        <RoutesTab
          isOpen={isGoRouteModalOpen}
          setIsOpen={setIsGoRouteModalOpen}
          onSelect={handleGoRoute}
        />
        <MapCanvas
          ref={mapCanvasRef}
          viewState={viewState}
          onMove={setViewState}
          routeData={routeData}
          routePins={routePins}
          selectedPinIndex={selectedPractitionerIndex}
          onMapLoaded={() => {
            setIsMapLoaded(true);
          }}
        />
      </div>

      <Drawer
        opened={isPractitionerCardOpen}
        onClose={() => setIsPractitionerCardOpen(false)}
        position="bottom"
        withCloseButton={false}
        classNames={{
          overlay: styles.drawerOverlay,
          inner: styles.drawerInner,
          content: styles.drawerContent,
        }}
      >
        <Box
          maw={1200}
          mx="auto"
        >
          <BaseballCard
            backHandler={() => setIsPractitionerCardOpen(false)}
            defaultTab={initialPractitionerCardTab}
            generalNotes=""
          />
        </Box>
      </Drawer>

      <Drawer
        opened={isRouteListDrawerOpen}
        onClose={() => setIsRouteListDrawerOpen(false)}
        position="bottom"
        classNames={{
          overlay: styles.drawerOverlay,
          inner: styles.drawerInner,
          content: styles.drawerContent,
        }}
      >
        <Box
          maw={1200}
          mx="auto"
        >
          <ViewRoute
            currentRoute={store.currentActiveRoute}
            onRouteChange={() => {}}
          />
        </Box>
      </Drawer>
    </>
  );
}
)

export default NavigationMode;